/**
 * Tempo presente do METAR em português — funções puras (testadas em
 * `meteo.test.ts`).
 *
 * O grupo de tempo presente é uma sequência de tokens (OMM, Anexo 3 da OACI):
 * intensidade (`-` fraco, `+` forte, `VC` nas vizinhanças), um descritor
 * (`TS` trovoada, `SH` pancadas, `BC` em bancos…) e fenômenos de duas letras
 * (`RA` chuva, `FU` fumaça, `HZ` névoa seca…). Na Amazônia da seca, o que mais
 * importa é `FU` e `HZ`: é o aeródromo dizendo que a visibilidade caiu por
 * fumaça — medido por gente, na hora, e não inferido do satélite.
 */

type Genero = 'm' | 'f';

const FENOMENOS: Readonly<Record<string, { nome: string; g: Genero }>> = {
  DZ: { nome: 'chuvisco', g: 'm' },
  RA: { nome: 'chuva', g: 'f' },
  SN: { nome: 'neve', g: 'f' },
  SG: { nome: 'grãos de neve', g: 'm' },
  IC: { nome: 'cristais de gelo', g: 'm' },
  PL: { nome: 'pelotas de gelo', g: 'f' },
  GR: { nome: 'granizo', g: 'm' },
  GS: { nome: 'granizo miúdo', g: 'm' },
  UP: { nome: 'precipitação', g: 'f' },
  BR: { nome: 'névoa úmida', g: 'f' },
  FG: { nome: 'nevoeiro', g: 'm' },
  FU: { nome: 'fumaça', g: 'f' },
  VA: { nome: 'cinza vulcânica', g: 'f' },
  DU: { nome: 'poeira', g: 'f' },
  SA: { nome: 'areia', g: 'f' },
  HZ: { nome: 'névoa seca', g: 'f' },
  PY: { nome: 'borrifo', g: 'm' },
  PO: { nome: 'redemoinhos de poeira', g: 'm' },
  SQ: { nome: 'tempestade de vento', g: 'f' },
  FC: { nome: 'nuvem funil', g: 'f' },
  SS: { nome: 'tempestade de areia', g: 'f' },
  DS: { nome: 'tempestade de poeira', g: 'f' },
};

/** Descritores que viram um adjetivo depois do fenômeno. */
const ADJETIVOS: Readonly<Record<string, [string, string]>> = {
  MI: ['baixo', 'baixa'],
  BC: ['em bancos', 'em bancos'],
  PR: ['parcial', 'parcial'],
  DR: ['levantado', 'levantada'],
  BL: ['soprado', 'soprada'],
  FZ: ['congelante', 'congelante'],
};

const DESCRITORES = new Set(['TS', 'SH', ...Object.keys(ADJETIVOS)]);

function intensidade(sinal: string, g: Genero, plural = false): string {
  if (sinal === '-') return plural ? (g === 'f' ? 'fracas' : 'fracos') : g === 'f' ? 'fraca' : 'fraco';
  if (sinal === '+') return plural ? 'fortes' : 'forte';
  return '';
}

function juntar(partes: string[]): string {
  return partes.length <= 1 ? (partes[0] ?? '') : `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}`;
}

/** Um token ("-TSRA", "VCSH", "FU", "+RA") em português, ou `null` se não for tempo presente. */
export function traduzirToken(token: string): string | null {
  const m = /^(-|\+|VC)?([A-Z]{2})?([A-Z]{2})*$/.exec(token);
  if (!m || token.length < 2) return null;
  let resto = token;
  let sinal = '';
  if (resto.startsWith('-') || resto.startsWith('+')) {
    sinal = resto[0]!;
    resto = resto.slice(1);
  }
  let vizinhanca = false;
  if (resto.startsWith('VC')) {
    vizinhanca = true;
    resto = resto.slice(2);
  }
  let descritor = '';
  if (resto.length >= 2 && DESCRITORES.has(resto.slice(0, 2))) {
    descritor = resto.slice(0, 2);
    resto = resto.slice(2);
  }
  const fen: { nome: string; g: Genero }[] = [];
  for (let i = 0; i < resto.length; i += 2) {
    const f = FENOMENOS[resto.slice(i, i + 2)];
    if (!f) return null;
    fen.push(f);
  }
  if (!descritor && fen.length === 0) return null;

  let frase: string;
  if (descritor === 'TS') {
    // "-TSRA": a intensidade é da precipitação, não da trovoada.
    const com = fen.map((f, i) => (i === 0 && sinal ? `${f.nome} ${intensidade(sinal, f.g)}` : f.nome));
    frase = com.length ? `trovoada com ${juntar(com)}` : 'trovoada';
  } else if (descritor === 'SH') {
    const f = fen[0] ?? FENOMENOS.RA!;
    const nomes = fen.length ? juntar(fen.map((x) => x.nome)) : f.nome;
    const adj = sinal ? ` ${intensidade(sinal, 'f', true)}` : '';
    frase = `pancadas de ${nomes}${adj}`;
  } else if (descritor) {
    const f = fen[0];
    if (!f) return null;
    const [m1, f1] = ADJETIVOS[descritor]!;
    frase = `${f.nome} ${f.g === 'f' ? f1 : m1}${sinal ? ` ${intensidade(sinal, f.g)}` : ''}`;
    if (fen.length > 1) frase = `${frase} e ${juntar(fen.slice(1).map((x) => x.nome))}`;
  } else {
    const [primeiro, ...outros] = fen;
    frase = sinal ? `${primeiro!.nome} ${intensidade(sinal, primeiro!.g)}` : primeiro!.nome;
    if (outros.length) frase = `${frase} com ${juntar(outros.map((x) => x.nome))}`;
  }
  return vizinhanca ? `${frase} nas vizinhanças` : frase;
}

/** O grupo inteiro ("-TSRA BR") → frases em português, na ordem do METAR. */
export function tempoPresente(wx: string | null | undefined): string[] {
  if (!wx) return [];
  return wx
    .trim()
    .split(/\s+/)
    .map(traduzirToken)
    .filter((x): x is string => !!x);
}

/** O METAR fala de fumaça ou névoa seca (visibilidade reduzida por partículas)? */
export function temFumaca(wx: string | null | undefined): boolean {
  return !!wx && /(^|\s)(-|\+|VC)?[A-Z]{0,2}(FU|HZ)(\s|$)/.test(wx);
}

/** Primeira letra maiúscula, para uma frase isolada. */
export function maiuscula(s: string): string {
  return s ? s[0]!.toUpperCase() + s.slice(1) : s;
}

/** Visibilidade em texto: "4 km", "800 m", "10 km ou mais". */
export function visibilidadeTexto(m: number | null | undefined): string | null {
  if (typeof m !== 'number' || !Number.isFinite(m)) return null;
  if (m >= 10_000) return '10 km ou mais';
  if (m >= 5000) return `${Math.round(m / 1000)} km`;
  if (m >= 1000) return `${(m / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km`;
  return `${Math.round(m / 50) * 50} m`;
}
